import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import L from "leaflet";
import { MapPin, Clock, Phone, Navigation } from "lucide-react";
import { PublicShell, useBrand } from "./PublicHome";
import { resolveImageUrl } from "../context/AppSettingsContext";

const LEAFLET_CSS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
const ensureLeafletCss = () => {
  if (document.querySelector(`link[href="${LEAFLET_CSS}"]`)) return;
  const l = document.createElement("link"); l.rel = "stylesheet"; l.href = LEAFLET_CSS; document.head.appendChild(l);
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const tq = () => { const t = new URLSearchParams(window.location.search).get("t") || localStorage.getItem("kl_tenant"); return t ? { headers: { "X-Tenant": t } } : {}; };
const PROV = { NA: "Napoli", CE: "Caserta", BN: "Benevento", SA: "Salerno" };
const card = { borderRadius: "var(--tk-radius-card, 20px)", boxShadow: "var(--tk-shadowCard, 0 6px 18px rgba(0,0,0,0.07))" };

function StoresMap({ stores, brand, selected, onSelect }) {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  useEffect(() => {
    if (!ref.current || mapRef.current) return;
    ensureLeafletCss();
    const m = L.map(ref.current, { scrollWheelZoom: false, attributionControl: true }).setView([40.9, 14.5], 9);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 18, attribution: "© OpenStreetMap" }).addTo(m);
    mapRef.current = m;
    layerRef.current = L.layerGroup().addTo(m);
    return () => { m.remove(); mapRef.current = null; };
  }, []);
  useEffect(() => {
    const m = mapRef.current, layer = layerRef.current;
    if (!m || !layer) return;
    layer.clearLayers();
    const pts = stores.filter((s) => s.lat && s.lon);
    pts.forEach((s) => {
      const active = s.id === selected;
      const icon = L.divIcon({ className: "", iconSize: [active ? 34 : 26, active ? 34 : 26], iconAnchor: [active ? 17 : 13, active ? 34 : 26],
        html: `<div style="width:100%;height:100%;background:${brand};border:3px solid #fff;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 3px 8px rgba(0,0,0,.3)"></div>` });
      L.marker([s.lat, s.lon], { icon }).addTo(layer).bindPopup(`<b>${s.name}</b><br/>${s.address}`).on("click", () => onSelect(s.id));
    });
    if (pts.length) m.fitBounds(L.latLngBounds(pts.map((s) => [s.lat, s.lon])), { padding: [28, 28], maxZoom: 13 });
  }, [stores, brand, selected, onSelect]);
  useEffect(() => {
    const s = stores.find((x) => x.id === selected);
    if (s?.lat && mapRef.current) mapRef.current.flyTo([s.lat, s.lon], 14, { duration: 0.6 });
  }, [selected, stores]);
  return <div ref={ref} className="h-64 w-full overflow-hidden bg-zinc-100" style={card} data-testid="stores-map" />;
}

export default function PublicStores() {
  const { brand } = useBrand();
  const [rows, setRows] = useState(null);
  const [prov, setProv] = useState("");
  const [sel, setSel] = useState(null);
  useEffect(() => { axios.get(`${API}/public/branches`, tq()).then(({ data }) => setRows(data || [])).catch(() => setRows([])); }, []);
  const provs = useMemo(() => [...new Set((rows || []).map((b) => b.province).filter(Boolean))].sort(), [rows]);
  const list = useMemo(() => (rows || []).filter((b) => !prov || b.province === prov).sort((a, b) => (a.city || "").localeCompare(b.city || "") || a.name.localeCompare(b.name)), [rows, prov]);
  return (
    <PublicShell title="Punti vendita" back="/home" testid="public-stores">
      {provs.length > 1 && (
        <div className="flex flex-wrap gap-2" data-testid="stores-prov-filter">
          <button type="button" onClick={() => setProv("")} className="px-3 py-1.5 text-xs font-bold ring-1" style={{ borderRadius: "var(--tk-radius-chip, 999px)", background: !prov ? brand : "#fff", color: !prov ? "#fff" : brand, borderColor: brand, "--tw-ring-color": brand }} data-testid="stores-prov-all">Tutte ({(rows || []).length})</button>
          {provs.map((p) => <button key={p} type="button" onClick={() => setProv(p)} className="px-3 py-1.5 text-xs font-bold ring-1" style={{ borderRadius: "var(--tk-radius-chip, 999px)", background: prov === p ? brand : "#fff", color: prov === p ? "#fff" : brand, "--tw-ring-color": brand }} data-testid={`stores-prov-${p}`}>{PROV[p] || p} ({(rows || []).filter((b) => b.province === p).length})</button>)}
        </div>
      )}
      {rows && rows.some((b) => b.lat) && <StoresMap stores={list} brand={brand} selected={sel} onSelect={setSel} />}
      <div className="space-y-3" data-testid="stores-list">
        {rows === null && <p className="text-sm text-zinc-500 text-center py-6">Caricamento…</p>}
        {rows && list.length === 0 && <p className="text-sm text-zinc-500 text-center py-6" data-testid="stores-empty">Nessun punto vendita.</p>}
        {list.map((b) => {
          const img = resolveImageUrl(b.store_image_url || b.gallery?.[0]);
          const open = sel === b.id;
          return (
            <article key={b.id} className="bg-white overflow-hidden" style={{ ...card, outline: open ? `2px solid ${brand}` : "none" }} data-testid={`store-${b.id}`}>
              {img && <img src={img} alt="" className="w-full h-32 object-cover" />}
              <button type="button" onClick={() => setSel(open ? null : b.id)} className="w-full text-left p-4 space-y-1.5" data-testid={`store-toggle-${b.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0"><h3 className="font-heading font-extrabold leading-tight" style={{ color: "var(--tk-ink, #111)" }}>{b.name}</h3>
                    <div className="text-xs mt-0.5 flex items-start gap-1" style={{ color: "var(--tk-textMuted, #71717a)" }}><MapPin className="h-3.5 w-3.5 mt-px shrink-0" /> {b.address}</div></div>
                  {b.province && <span className="shrink-0 px-2 py-0.5 text-[10px] font-extrabold text-white" style={{ background: brand, borderRadius: "var(--tk-radius-chip, 999px)" }}>{b.province}</span>}
                </div>
                {b.hours && <div className="text-xs whitespace-pre-line flex items-start gap-1" style={{ color: "var(--tk-text, #3f3f46)" }} data-testid={`store-hours-${b.id}`}><Clock className="h-3.5 w-3.5 mt-px shrink-0" /> {b.hours}</div>}
                {b.phone && <div className="text-xs flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {b.phone}</div>}
                {b.services?.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-1" data-testid={`store-services-${b.id}`}>
                    {b.services.map((s) => <span key={s} className="px-2 py-0.5 text-[10px] font-semibold" style={{ background: "var(--tk-primarySoft, #f4f4f5)", color: "var(--tk-onPrimarySoft, #3f3f46)", borderRadius: "var(--tk-radius-chip, 999px)" }}>{s}</span>)}
                  </div>
                )}
              </button>
              <div className="px-4 pb-4 flex gap-2">
                <a href={b.lat ? `https://www.google.com/maps/dir/?api=1&destination=${b.lat},${b.lon}` : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(b.address)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-white" style={{ background: brand, borderRadius: "var(--tk-radius-button, 12px)" }} data-testid={`store-directions-${b.id}`}><Navigation className="h-3.5 w-3.5" /> Indicazioni</a>
                {b.lat && <button type="button" onClick={() => { setSel(b.id); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold ring-1" style={{ color: brand, "--tw-ring-color": brand, borderRadius: "var(--tk-radius-button, 12px)" }} data-testid={`store-on-map-${b.id}`}><MapPin className="h-3.5 w-3.5" /> Sulla mappa</button>}
              </div>
            </article>
          );
        })}
      </div>
    </PublicShell>
  );
}
